import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import apiFiles from 'api/files';
import apiProducts from 'api/products';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import TrashIcon from 'components/layout/icons/TrashIcon';
import IFile from 'interfaces/forms/IFile';
import getNextObjectKey from 'services/numbers/getNextObjectKey';
import shrinkImage from 'services/files/shrinkImage';
import translations from 'services/translations/translations';

// The type of the file of the product image among the files of the product
export const PRODUCT_IMAGE = 'product-image';

/**
 * The small, light picture of the product (tuotekuva): one, made light in the browser
 * before it is saved with the product. It is opened from the rows of the positions.
 */
const ProductImage = ({
  files,
  setFiles,
}: {
  files: IFile[];
  setFiles: (files: IFile[]) => void;
}): JSX.Element => {
  const { t } = useTranslation([translations.products, translations.common]);
  const input = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<null | string>(null);
  const [uploading, setUploading] = useState(false);
  // A picture dragged over the image, and a dropped file that was not a picture
  const [dragging, setDragging] = useState(false);
  const [notImage, setNotImage] = useState(false);

  const image = files.find((file) => file.type === PRODUCT_IMAGE);

  // The picture as chosen, or as saved
  useEffect(() => {
    let url: null | string = null;
    let active = true;
    if (!image?.id) {
      setPreview(null);
      return undefined;
    }

    const show = (blob: Blob) => {
      url = URL.createObjectURL(blob);
      if (active) {
        setPreview(url);
      }
    };

    if (image.file) {
      show(image.file);
    } else {
      apiProducts.files
        .get(image.id)
        .then((response: { data: BlobPart; headers: Record<string, string> }) =>
          show(
            new Blob([response.data], {
              type: response.headers['content-type'],
            })
          )
        )
        .catch(() => setPreview(null));
    }

    return () => {
      active = false;
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [image?.id, image?.file]);

  // A picture chosen or dropped: made light and saved as the image of the product
  const takeImage = async (chosen: File) => {
    setNotImage(false);
    setUploading(true);
    try {
      const light = await shrinkImage(chosen);
      const result = await apiFiles.post(light, null);
      setFiles([
        ...files.filter((file) => file.type !== PRODUCT_IMAGE),
        {
          file: light,
          id: result.id.toString(),
          key: getNextObjectKey(files, 'key'),
          name: light.name,
          type: PRODUCT_IMAGE,
        },
      ]);
    } finally {
      setUploading(false);
    }
  };

  const choose = (event: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0];
    // The same picture can be chosen again
    event.target.value = '';
    if (chosen) {
      takeImage(chosen);
    }
  };

  // A picture can also be dragged onto the image from the computer; other files are not taken
  const canDrop = !uploading;

  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!canDrop) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setDragging(true);
  };

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (!canDrop) {
      return;
    }

    const dropped = Array.from(event.dataTransfer.files).find((file) =>
      file.type.startsWith('image/')
    );
    setNotImage(dropped === undefined && event.dataTransfer.files.length > 0);
    if (dropped) {
      takeImage(dropped);
    }
  };

  const chooseLabel = image
    ? t('productImage.Change')
    : t('productImage.Choose');

  const remove = () =>
    setFiles(files.filter((file) => file.type !== PRODUCT_IMAGE));

  return (
    <div className="product-image">
      <h3>{t('productImage.Title')}</h3>
      <p className="product-image-info">{t('productImage.Info')}</p>
      <div className="product-image-row">
        <div
          className={`product-image-drop${dragging ? ' dragging' : ''}`}
          onDragLeave={() => setDragging(false)}
          onDragOver={onDragOver}
          onDrop={onDrop}
          title={t('productImage.Drop')}
        >
          {preview ? (
            <img
              alt={t('productImage.Title')}
              className="product-image-preview"
              src={preview}
            />
          ) : (
            <div className="product-image-empty">
              {t('productImage.No image')}
              <small>{t('productImage.Drop')}</small>
            </div>
          )}
        </div>
        <div className="product-image-actions">
          <input
            accept="image/*"
            hidden
            onChange={choose}
            ref={input}
            type="file"
          />
          <PrimaryButton
            disabled={uploading}
            onClick={() => input.current?.click()}
          >
            {uploading ? t('productImage.Saving') : chooseLabel}
          </PrimaryButton>
          {image ? <TrashIcon onClick={remove} /> : null}
        </div>
      </div>
      {notImage ? (
        <p className="product-image-error" role="alert">
          {t('productImage.Not an image')}
        </p>
      ) : null}
    </div>
  );
};

export default ProductImage;
